import cx from 'classnames'
import { useEffect, useState } from 'react'
import * as AppPreferencesClient from 'api/clients/AppPreferencesClient'
import { ReactComponent as MinusIcon } from 'assets/icons/minus.svg'
import { Spinner } from 'common/Spinner/Spinner'
import { Checkbox } from 'components/Form/Checkbox/Checkbox'
import SettingsStyles from 'components/Settings/SettingsModal.module.scss'
import { Cell } from 'components/Table/components/Cell/Cell'
import { Header } from 'components/Table/components/Header/Header'
import { Headers } from 'components/Table/components/Headers/Headers'
import { Row } from 'components/Table/components/Row/Row'
import { Table } from 'components/Table/Table'
import useUserPreferences from 'hooks/useUserPreferences'
import { NotificationChannel, NotificationGroup, NotificationSettings, Workspace } from 'types'

import Styles from './Notifications.module.scss'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Text } from 'blaze-ui/Text/Text'

type SettingMetadataItem = {
  heading: string
  description: string
  availableChannels: NotificationChannel[]
}
type SettingMetadata = {
  [key in NotificationGroup]: SettingMetadataItem
}
export const SETTINGS_METADATA: SettingMetadata = {
  documentAccess: {
    heading: 'Document access',
    description: 'Notifications when someone wants access or when someones shares a document with you.',
    availableChannels: ['inbox', 'email'],
  },
  reviews: {
    heading: 'Reviews',
    description: 'Notifications when someone requests a review or approval.',
    availableChannels: ['inbox', 'email'],
  },
  comments: {
    heading: 'Comments',
    description: 'Notification when someone comments on your documents or mentions you in a comment.',
    availableChannels: ['inbox', 'email'],
  },
  tasks: {
    heading: 'Tasks',
    description: 'Notification when tasks are created on your document or assigned to you.',
    availableChannels: ['inbox', 'email'],
  },
  reminders: {
    heading: 'Reminders',
    description: 'Notification when tasks or reviews due dates are approaching.',
    availableChannels: ['email'],
  },
  scheduledPostsPublished: {
    heading: 'Scheduled Posts Published',
    description: 'Notification when a scheduled post is successfully published to an external platform.',
    availableChannels: ['email'],
  },
  scheduledPostsFailedToPublish: {
    heading: 'Scheduled Posts Failed to Publish',
    description: 'Notification when a scheduled post fails to publish to an external platform.',
    availableChannels: ['email'],
  },
}
interface Props {
  workspace: Workspace
}
export const Notifications = ({ workspace }: Props) => {
  const { isMobile } = useResponsiveDesign()
  const {
    data: userPreferences,
    isLoading: userPreferencesLoading,
    refetch: refetchUserPreferences,
  } = useUserPreferences(workspace?.id)
  const [settings, setSettings] = useState<NotificationSettings>(userPreferences?.notifications as NotificationSettings)
  async function updateSettings(newSettings: NotificationSettings) {
    setSettings(newSettings)
    await AppPreferencesClient.updateUserAppPreferences({ notifications: newSettings })
    refetchUserPreferences()
  }
  function onCellCheck(group: NotificationGroup, channel: NotificationChannel, isSelected: boolean) {
    const newSettings = {
      ...settings,
      [group]: {
        ...settings[group],
        [channel]: isSelected,
      },
    }
    updateSettings(newSettings)
  }
  function onColumnCheck(channel: NotificationChannel, isSelected: boolean) {
    const newSettings = Object.keys(SETTINGS_METADATA).reduce(
      (prev, curr) => ({ ...prev, [curr]: { ...settings[curr as NotificationGroup], [channel]: isSelected } }),
      {},
    )
    updateSettings(newSettings)
  }
  function markedCheckboxesByColumn(channel: NotificationChannel) {
    const allGroups = Object.entries(SETTINGS_METADATA).filter(([_, data]) => data.availableChannels.includes(channel))
    const enabledGroups = allGroups.filter(([key, _]) => settings?.[key as NotificationGroup]?.[channel])
    switch (enabledGroups.length / allGroups.length) {
      case 1:
        return 'all'
      case 0:
        return 'none'
      default:
        return 'some'
    }
  }

  const markedCheckboxesByColumns = {
    inbox: markedCheckboxesByColumn('inbox'),
    email: markedCheckboxesByColumn('email'),
  }
  useEffect(() => {
    setSettings(userPreferences?.notifications as NotificationSettings)
  }, [userPreferences])
  return (
    <>
      {!isMobile && (
        <header className={SettingsStyles.subHeader}>
          <Heading level={3}>Notifications {userPreferencesLoading && <Spinner />}</Heading>
        </header>
      )}
      {!userPreferencesLoading && settings && (
        <Table className={Styles.table}>
          <Headers className={Styles.headerContainer}>
            {!isMobile && <Header width={500} className={Styles.header} />}
            <Header width={isMobile ? undefined : 100} className={Styles.header}>
              <Checkbox
                size={isMobile ? 'lg' : 'md'}
                variant="primary"
                isSelected={markedCheckboxesByColumns.inbox !== 'none'}
                checkIcon={markedCheckboxesByColumns.inbox === 'some' ? <MinusIcon /> : undefined}
                onChange={(isSelected) => onColumnCheck('inbox', isSelected)}
                aria-label="All inbox notifications"
                className={Styles.checkbox}
              />
              <span className={Styles.headerLabel}>Notifications</span>
            </Header>
            <Header width={isMobile ? undefined : 100} className={Styles.header}>
              <Checkbox
                size={isMobile ? 'lg' : 'md'}
                variant="primary"
                isSelected={markedCheckboxesByColumns.email !== 'none'}
                checkIcon={markedCheckboxesByColumns.email === 'some' ? <MinusIcon /> : undefined}
                onChange={(isSelected) => onColumnCheck('email', isSelected)}
                aria-label="All email notifications"
                className={Styles.checkbox}
              />
              <span className={Styles.headerLabel}>Email</span>
            </Header>
          </Headers>
          {Object.entries(SETTINGS_METADATA).map(([key, data]) => (
            <Row size="lg" key={key} className={Styles.row}>
              {isMobile && (
                <>
                  <Cell className={Styles.labelCell}>
                    <Heading level={5}>{data.heading}</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      {data.description}
                    </Text>
                  </Cell>
                  <div className={Styles.checkboxes}>
                    {data.availableChannels.includes('inbox') && (
                      <Cell className={Styles.cell}>
                        <Checkbox
                          size="lg"
                          variant="primary"
                          className={Styles.checkbox}
                          isSelected={settings[key as NotificationGroup]?.inbox}
                          onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'inbox', isSelected)}
                          aria-label={`Inbox notifications for ${data.heading}`}
                        />
                        <span className={Styles.checkboxLabel}>Notifications</span>
                      </Cell>
                    )}
                    {data.availableChannels.includes('email') && (
                      <Cell className={Styles.cell}>
                        <Checkbox
                          size="lg"
                          variant="primary"
                          className={Styles.checkbox}
                          isSelected={settings[key as NotificationGroup]?.email}
                          onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'email', isSelected)}
                          aria-label={`Email notifications for ${data.heading}`}
                        />
                        <span className={Styles.checkboxLabel}>Email</span>
                      </Cell>
                    )}
                  </div>
                </>
              )}
              {!isMobile && (
                <>
                  <Cell width={500} className={Styles.labelCell}>
                    <Heading level={5}>{data.heading}</Heading>
                    <Text variant="secondary" color="var(--dark-60)">
                      {data.description}
                    </Text>
                  </Cell>
                  <Cell
                    width={100}
                    className={cx(Styles.cell, { [Styles.cellHidden]: !data.availableChannels.includes('inbox') })}
                  >
                    {data.availableChannels.includes('inbox') ? (
                      <Checkbox
                        size="lg"
                        variant="primary"
                        className={Styles.checkbox}
                        isSelected={settings[key as NotificationGroup]?.inbox}
                        onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'inbox', isSelected)}
                        aria-label={`Inbox notifications for ${data.heading}`}
                      />
                    ) : null}
                  </Cell>
                  <Cell
                    width={100}
                    className={cx(Styles.cell, { [Styles.cellHidden]: !data.availableChannels.includes('email') })}
                  >
                    {data.availableChannels.includes('email') ? (
                      <Checkbox
                        size="lg"
                        variant="primary"
                        className={Styles.checkbox}
                        isSelected={settings[key as NotificationGroup]?.email}
                        onChange={(isSelected) => onCellCheck(key as NotificationGroup, 'email', isSelected)}
                        aria-label={`Email notifications for ${data.heading}`}
                      />
                    ) : null}
                  </Cell>
                </>
              )}
            </Row>
          ))}
        </Table>
      )}
    </>
  )
}
